import { SUBJECT_FIELD_SCHEMA } from "./fieldSchema";
import type { SubjectGrid } from "./types";
import { conflictingLotCandidates } from "./lotConflict";

/**
 * ONE DEFINITION OF WHAT A SUBJECT GRID IS (2026-08-04).
 *
 * These two functions lived inside `CmaAmbFlow`, which was fine while the flow was the only thing
 * that built a subject grid. The address-first intake screen now looks a property up BEFORE any
 * file exists, so it builds one too — and two copies of "what does a filled field look like" is
 * exactly how a source tag ends up saying one thing on one screen and another on the next.
 *
 * Nothing about either function's behaviour changed in the move.
 */

/**
 * WHERE A ROW'S SOURCE TAG COMES FROM (2026-08-11, the source cascade).
 *
 * A plain string is what every caller passed before the cascade existed and still means what it
 * always did: one payload, one source, one tag on every row it fills. A FUNCTION is what the
 * cascade needs, because its whole point is that one resolved subject can be an uploaded file's GLA
 * beside a public-record lot size beside a figure the agent typed — provenance per field, not per
 * payload (`source_cascade.merge_layers`). Passing a single string for such a subject would flatten
 * three true statements into one false one.
 *
 * A resolver returning null/"" leaves the row untagged, which reads exactly as it does today: a
 * value with no stated source. It is never silently replaced with a guess.
 */
export type SourceTagFor = string | ((key: string) => string | null | undefined);

function tagFor(source: SourceTagFor, key: string): string | null {
  const tag = typeof source === "function" ? source(key) : source;
  return typeof tag === "string" && tag.trim() ? tag : null;
}

/** A grid built from a freshly-fetched or freshly-extracted subject dict. Every populated field is
 * pre-checked and carries an honest source tag; every absent field is unchecked and BLANK — never
 * a zero, never a borrowed value (VI-3). */
export function gridFromSubject(subject: Record<string, unknown>, sourceTag: SourceTagFor): SubjectGrid {
  const grid: SubjectGrid = {};
  for (const def of SUBJECT_FIELD_SCHEMA) {
    const value = subject[def.key];
    const present = value !== undefined && value !== null && value !== "";
    grid[def.key] = {
      key: def.key,
      value: present ? (value as string | number | boolean) : null,
      sourceTag: present ? tagFor(sourceTag, def.key) : null,
      checked: present,
    };
  }
  const lot = unresolvedLot(subject);
  if (lot) grid.lot_sf = lot;
  return grid;
}

/**
 * THE LOT ROW WHEN THE COUNTY AND THE MLS DISAGREE (2026-11-03, `lotConflict.ts`).
 *
 * Returns the unresolved `lot_sf` row, or null when this subject has no conflict, in which case the
 * caller does exactly what it did before this existed. Unresolved means: no value, no source tag,
 * not checked, with both candidates attached. Whatever single `lot_sf` the payload also carried is
 * deliberately NOT used. That figure is the silent choice this exists to end.
 */
function unresolvedLot(subject: Record<string, unknown>): SubjectGrid[string] | null {
  const candidates = conflictingLotCandidates(subject.lot_candidates);
  if (!candidates) return null;
  return { key: "lot_sf", value: null, sourceTag: null, checked: false, candidates, candidateIndex: null };
}

/** Fills ONLY currently-unchecked fields from a newly-fetched source, never overwriting a value the
 * human already confirmed. The client half of VI-4c's "merge, never restart" rule. */
export function mergeGrid(
  current: SubjectGrid,
  incoming: Record<string, unknown>,
  sourceTag: SourceTagFor
): SubjectGrid {
  const next = { ...current };
  for (const def of SUBJECT_FIELD_SCHEMA) {
    if (next[def.key]?.checked) continue; // never overwrite a human-confirmed field
    const value = incoming[def.key];
    if (value === undefined || value === null || value === "") continue;
    next[def.key] = {
      key: def.key,
      value: value as string | number | boolean,
      sourceTag: tagFor(sourceTag, def.key),
      checked: true,
    };
  }
  // A conflict never overwrites a lot size the human already confirmed (the loop above skips it),
  // and never fills one in either: it leaves the row unresolved for the agent to pick.
  if (!current.lot_sf?.checked) {
    const lot = unresolvedLot(incoming);
    if (lot) next.lot_sf = lot;
  }
  return next;
}

/** How many fields in a grid actually carry a value. What the agent means by "did it find
 * anything" — and the number the intake screen reports back to them. */
export function filledCount(grid: SubjectGrid | null | undefined): number {
  if (!grid) return 0;
  return Object.values(grid).filter(
    (f) => f && f.value !== null && f.value !== undefined && f.value !== ""
  ).length;
}

/** A field the human has actually confirmed: ticked AND carrying a value. One spelling of the test
 * `ReadinessGate.missingRequiredFields`, `ConfirmGrid` and the carry-over split below all apply. */
export function isFieldConfirmed(grid: SubjectGrid | null | undefined, key: string): boolean {
  const f = grid?.[key];
  return !!f?.checked && f.value != null && f.value !== "";
}

/** Every field the agent has confirmed, in schema order. */
export function confirmedKeys(grid: SubjectGrid | null | undefined): string[] {
  return SUBJECT_FIELD_SCHEMA.filter((f) => isFieldConfirmed(grid, f.key)).map((f) => f.key);
}

/**
 * Of a `carryOver().added` list, the fields a SOURCE actually landed — not the ones the agent
 * typed on the screen that is about to describe them back to them (2026-08-06, owner defect).
 *
 * `added` is "confirmed now, not confirmed on the earlier step", which is the right split for
 * deciding which rows to show. It is the WRONG list to headline as "details have landed since",
 * under a sentence reading "each row says where it came from — untick anything you don't want
 * used": a value the agent entered thirty seconds ago on this very screen is not an arrival from
 * anywhere, and announcing it as one is how a summary that was supposed to shorten the work ended
 * up manufacturing some. The source tag is exactly the test, and "You entered" fails it.
 *
 * Nothing is hidden by this. A typed field's row is still on the screen, still ticked, still tagged.
 */
export function sourcedAdditions(added: string[], grid: SubjectGrid | null | undefined): string[] {
  return added.filter((k) => {
    const tag = grid?.[k]?.sourceTag;
    return !!tag && tag !== "You entered";
  });
}

// ------------------------------------------------------------------------------------------- //
// THE ACRES ROW, WHICH USED TO BE A TRAP (2026-08-06)                                          //
// ------------------------------------------------------------------------------------------- //
//
// `acres` sits directly beneath `lot_sf` under a near-identical label ("Lot size" / "Lot size
// (acres)"). An agent typed 10010 — the square footage confirmed one row above — into acres.
// NOTHING on the client looked at it. The only check in the product was `engine.amb`'s lot-conflict
// rule, which runs at COMPUTE time: the build died after a twenty-page OCR pass with a message
// blaming the data, and the owner could not produce a report at all.
//
// The owner's instruction was "lot size should be reverse math compatible and should not cause
// things to break". Reverse math is exactly what this does: acres x 43,560 must reproduce the
// confirmed square footage, and it says so at the keystroke, in plain language, next to the field —
// while the number is still under the agent's cursor and costs one keypress to fix.
//
// KEPT IN LOCKSTEP with `engine.amb.reconcile_lot` / `validate_subject`: same 43,560, same 2%
// tolerance, same "a residential lot is not thousands of acres" bound. The server half resolves the
// mis-entry rather than halting on it; this half means the agent rarely gets that far.
export const SQFT_PER_ACRE = 43560;

/** Above this, a figure is not a residential lot size in acres. Mirrors
 * `engine.amb.MAX_RESIDENTIAL_ACRES`. */
export const MAX_RESIDENTIAL_ACRES = 100;

/** Same 2% agreement the engine's lot-conflict check requires. */
const LOT_TOLERANCE = 0.02;

function asNumber(v: unknown): number | null {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() !== "" && Number.isFinite(Number(v))) return Number(v);
  return null;
}

/**
 * What is wrong with the acres box right now, in words an agent can act on — or null when there is
 * nothing to say (which includes the ordinary case of an empty optional row).
 *
 * Three things it catches, in the order they matter:
 *   1. The square footage typed into the acres row. Named explicitly, because it is the mistake
 *      that actually happened and because the server resolves this one rather than halting.
 *   2. A figure that is not a residential lot in acres at all, with no square footage to compare.
 *   3. An acres figure that is plausible but does not reproduce the confirmed square footage. This
 *      is a genuine disagreement between two numbers and only the agent can say which is right —
 *      so it is stated, not resolved.
 */
export function acresConflictMessage(acres: unknown, lotSf: unknown): string | null {
  const a = asNumber(acres);
  if (a === null || a <= 0) return null;              // empty/blank optional row — nothing to say
  const lot = asNumber(lotSf);
  const looksLikeSquareFeet =
    a > MAX_RESIDENTIAL_ACRES && lot != null && lot > 0 && Math.abs(a - lot) / lot <= LOT_TOLERANCE;
  if (looksLikeSquareFeet) {
    return (
      `That is the square footage, not the acreage — ${a.toLocaleString()} is the same figure as ` +
      `the lot size confirmed above. A lot this size is about ` +
      `${(lot! / SQFT_PER_ACRE).toFixed(3)} acres. Clear this row and the confirmed square ` +
      `footage is used; leaving it will not stop the report, but the row is telling us something ` +
      `we know is not true.`
    );
  }
  if (a > MAX_RESIDENTIAL_ACRES) {
    return (
      `${a.toLocaleString()} acres is about ${(a / 640).toFixed(1)} square miles. If this is the ` +
      `lot in square feet, it belongs in the "Lot size" row above; this row is only for a sheet ` +
      `that gives acres and no square footage.`
    );
  }
  if (lot != null && lot > 0 && Math.abs(a * SQFT_PER_ACRE - lot) / lot > LOT_TOLERANCE) {
    return (
      `${a} acres works out to ${Math.round(a * SQFT_PER_ACRE).toLocaleString()} sq ft, but the ` +
      `lot size above says ${Math.round(lot).toLocaleString()} sq ft. One of the two is wrong — ` +
      `correct it from the record, or clear this row and keep the square footage.`
    );
  }
  return null;
}

export interface SubjectCarryOver {
  /** Confirmed on the EARLIER step. These are shown quietly on the later one and never asked
   * again — see `ConfirmGrid`'s `quietKeys`. */
  carried: string[];
  /** Confirmed now but not on the earlier step: the uploaded file added it, a public-records fetch
   * filled it, or the agent typed it here. */
  added: string[];
}

/**
 * WHAT IS ACTUALLY NEW ON THE SECOND SCREEN (2026-08-05, owner: "the tool makes us confirm the
 * subject file multiple times lets make sure this is intuitive for the agent to walk through").
 *
 * Step 1 asks for the address and shows what the public record holds. Step 2 used to render the
 * IDENTICAL 24-row form over a second piece of state, with a fresh "correct anything that's wrong"
 * instruction — so the agent ticked twenty rows, waited out an OCR pass, and landed on the same
 * twenty rows. This split is what lets step 2 lead with the short list that changed.
 *
 * `carried` IS DELIBERATELY FROZEN ON THE EARLIER GRID, not recomputed from the live one. If it
 * asked "is this still confirmed?" then unticking a row inside the quiet block would move that row
 * out from under the pointer that just clicked it. Membership is decided by where the fact came
 * from; whether it is still ticked is a live question the gate answers, and `ConfirmGrid` force-
 * expands the block rather than letting an unticked REQUIRED row hide inside it.
 */
export function carryOver(
  earlier: SubjectGrid | null | undefined,
  current: SubjectGrid
): SubjectCarryOver {
  const carried: string[] = [];
  const added: string[] = [];
  for (const def of SUBJECT_FIELD_SCHEMA) {
    if (isFieldConfirmed(earlier, def.key)) carried.push(def.key);
    else if (isFieldConfirmed(current, def.key)) added.push(def.key);
  }
  return { carried, added };
}

/** Bookkeeping the provider attaches to every response. Not facts about the property, and not
 * things to count, show, or report as "found". `_`-prefixed keys are excluded by the rule below;
 * `confirmed` is the one that does not follow that convention and has to be named. */
const PROVIDER_BOOKKEEPING = new Set(["confirmed", "clip", "cached"]);

/** The fields a provider payload actually landed, ignoring its bookkeeping keys.
 *
 * NOT `Object.keys(data).length > 0`: `cotality.project_subject` ALWAYS returns `_source`,
 * `confirmed`, `pool`, `garage` and `construction`, so that test is true even when every mapped
 * field came back null. It reported success on a fill that changed nothing — the grid stayed empty,
 * the blocker stayed red, and the status line said it had worked.
 *
 * `confirmed` is excluded explicitly (2026-08-04). It is ALWAYS present and always `false`, and
 * `false` is not null/undefined/"" — so a lookup that matched no property at all still counted one
 * "field" and was reported as a success, on the exact address the owner's screenshot shows failing.
 * A count the agent reads has to count only things they can see. */
export function landedFields(data: Record<string, unknown> | null | undefined): [string, unknown][] {
  return Object.entries(data ?? {}).filter(
    ([k, v]) => !k.startsWith("_") && !PROVIDER_BOOKKEEPING.has(k)
      && v !== null && v !== undefined && v !== ""
  );
}

/**
 * A county CAD subject record -> the keys the subject grid actually uses.
 *
 * THE DEFECT THIS CLOSES (2026-08-04). `/api/subject` (`ingestion/registry.py`'s
 * `subject_lookup`) returns the assessor's own vocabulary:
 *
 *     {address, prop_id, living_area_sqft, year_built, beds, baths, assessed_value,
 *      market_value, state, county}
 *
 * `SUBJECT_FIELD_SCHEMA` keys on `gla`, `year` and `full_baths`, and `mergeGrid` only fills keys
 * that exist in the schema. So a county hit filled `address` — a field the agent had already
 * typed — and NOTHING else, while the screen said "Filled from the {county} County record.
 * Review the fields below." with nothing new below to review. Every name that mattered missed by
 * a synonym.
 *
 * `baths` IS NOT `full_baths`, AND IS NOT MAPPED. The CAD carries a single bath figure that does
 * not split full from half — "2.1" in Texas convention means two full and one half, but the field
 * is not reliably formatted and the engine's bath adjustment prices the two separately and is
 * gated on `subj.baths_known`. Guessing the split here would silently feed a fabricated half-bath
 * into a priced line. The number is left for the agent to enter, which is the same answer this
 * codebase gives everywhere else: a gap it can name beats a value it invented.
 *
 * `assessed_value` / `market_value` are deliberately absent too. An assessor's value is not a
 * market value and must never seed one — that is the entire premise of the product.
 */
const COUNTY_SUBJECT_KEY_MAP: Record<string, string> = {
  living_area_sqft: "gla",
  year_built: "year",
  beds: "beds",
  address: "address",
  // Lot size and garage AREA (2026-10-18, `cad_picker.SUBJECT_KEY_MAP`). Garage area fills the
  // descriptive "Garage area" row, never the bay count.
  land_area_sqft: "lot_sf",
  garage_area_sqft: "garage_sqft",
};

export function countySubjectToGrid(subject: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [from, to] of Object.entries(COUNTY_SUBJECT_KEY_MAP)) {
    const v = subject[from];
    if (v !== undefined && v !== null && v !== "") out[to] = v;
  }
  // A record that already speaks the grid's vocabulary (a future source, or a CAD row already
  // normalized upstream) passes straight through — the rename is additive, never a filter.
  for (const [k, v] of Object.entries(subject)) {
    if (k in out || k in COUNTY_SUBJECT_KEY_MAP) continue;
    if (v !== undefined && v !== null && v !== "") out[k] = v;
  }
  return out;
}
